import {Caption, Table as ExpoTable, TBody, TFoot, THead, TR} from "@expo/html-elements";
import React, {useCallback, useContext, useMemo} from "react";
import type {View} from "react-native";

import {isReactElement, PointerDownIcon, PointerUpIcon} from "../../../react";
import {Box} from "../box";
import {HStack} from "../hstack";
import {Pressable} from "../pressable";
import {Text} from "../text";

import {
    tableBodyStyle,
    tableCaptionStyle,
    tableCellContentStyle,
    tableCellStyle,
    tableFooterStyle,
    tableHeadContentStyle,
    tableHeaderStyle,
    tableHeadStyle,
    tableRowStyleStyle,
    tableStyle,
    tableWrapperStyle
} from "./styles";
import {TableContext, TableHeaderFooterContext} from "./TableContext";
import TablePagination from "./TablePagination";
import type {TableCellProps, TableHeadProps, TableProps, TableRowProps} from "./types";

type ITableProps = React.ComponentProps<typeof ExpoTable> & TableProps;
type ITableHeaderProps = React.ComponentProps<typeof THead>;
type ITableBodyProps = React.ComponentProps<typeof TBody>;
type ITableFooterProps = React.ComponentProps<typeof TFoot>;
type ITableRowProps = React.ComponentProps<typeof TR> & TableRowProps;
type ITableCaptionProps = React.ComponentProps<typeof Caption>;

const Table = React.forwardRef<React.ComponentRef<typeof ExpoTable | typeof Box>, ITableProps>(
    ({className, size = "md", gridTemplateColumnsClass, overflow, children, ...props}, ref) => {
        const tableValue = useMemo(
            () => ({size, gridTemplateColumnsClass}),
            [size, gridTemplateColumnsClass]
        );

        return (
            <TableContext.Provider value={tableValue}>
                {process.env.IS_WEB ? (
                    <Box
                        ref={ref as React.Ref<View>}
                        className={tableWrapperStyle({
                            class: className,
                            visible: overflow === "visible"
                        })}
                    >
                        <Box
                            className={tableStyle({
                                useGridTemplate: !!gridTemplateColumnsClass,
                                className: gridTemplateColumnsClass
                            })}
                            role="table"
                        >
                            {children}
                        </Box>
                    </Box>
                ) : (
                    <ExpoTable
                        // @ts-expect-error
                        ref={ref}
                        className={tableStyle({class: className})}
                        {...props}
                    >
                        {children}
                    </ExpoTable>
                )}
            </TableContext.Provider>
        );
    }
);

const TableHeader = React.forwardRef<React.ComponentRef<typeof THead>, ITableHeaderProps>(
    ({className, children, ...props}, ref) => {
        return (
            <TableHeaderFooterContext.Provider
                value={useMemo(() => {
                    return {
                        type: "header"
                    };
                }, [])}
            >
                {process.env.IS_WEB ? (
                    children
                ) : (
                    <THead
                        // @ts-expect-error
                        ref={ref}
                        className={tableHeaderStyle({class: className})}
                        {...props}
                    >
                        {children}
                    </THead>
                )}
            </TableHeaderFooterContext.Provider>
        );
    }
);

const TableBody = React.forwardRef<React.ComponentRef<typeof TBody>, ITableBodyProps>(
    ({className, children, ...props}, ref) => {
        if (process.env.IS_WEB) {
            return <>{children}</>;
        }

        return (
            <TBody
                // @ts-expect-error
                ref={ref}
                className={tableBodyStyle({class: className})}
                {...props}
            >
                {children}
            </TBody>
        );
    }
);

const TableFooter = React.forwardRef<React.ComponentRef<typeof TFoot>, ITableFooterProps>(
    ({className, children, ...props}, ref) => {
        return (
            <TableHeaderFooterContext.Provider
                value={useMemo(() => {
                    return {
                        type: "footer"
                    };
                }, [])}
            >
                {process.env.IS_WEB ? (
                    children
                ) : (
                    <TFoot
                        // @ts-expect-error
                        ref={ref}
                        className={tableFooterStyle({class: className})}
                        {...props}
                    />
                )}
            </TableHeaderFooterContext.Provider>
        );
    }
);

const TableHead = React.forwardRef<React.ComponentRef<typeof Box>, TableHeadProps>(
    ({className, align = "start", sortDir, children, onPress}, ref) => {
        const {size, gridTemplateColumnsClass} = useContext(TableContext);

        return (
            <Box
                ref={ref}
                className={tableHeadStyle({
                    className,
                    size,
                    useGridTemplate: !!gridTemplateColumnsClass
                })}
                role="columnheader"
                testID="table-header"
            >
                {typeof onPress === "function" ? (
                    <Pressable className={tableHeadContentStyle({align})} onPress={onPress}>
                        <HStack className="items-center" space="xs">
                            <Text
                                className="w-max"
                                color="blueGray.600"
                                variant={size === "md" ? "table" : "sm"}
                            >
                                {children}
                            </Text>
                            {sortDir === "asc" ? (
                                <PointerUpIcon fill="blueGray.400" size="xs" />
                            ) : sortDir === "desc" ? (
                                <PointerDownIcon fill="blueGray.400" size="xs" />
                            ) : null}
                        </HStack>
                    </Pressable>
                ) : (
                    <Box className={tableHeadContentStyle({align})}>
                        <Text
                            className="w-max"
                            color="blueGray.600"
                            variant={size === "md" ? "table" : "sm"}
                        >
                            {children}
                        </Text>
                    </Box>
                )}
            </Box>
        );
    }
);

const TableRow = React.forwardRef<React.ComponentRef<typeof TR>, ITableRowProps>(
    ({className, row, children, onRowClick, ...props}, ref) => {
        const {type} = useContext(TableHeaderFooterContext);
        const {gridTemplateColumnsClass} = useContext(TableContext);

        const handleRowClick = useCallback(() => {
            if (row) {
                onRowClick?.(row);
            }
        }, [onRowClick, row]);

        if (process.env.IS_WEB) {
            if (onRowClick) {
                return (
                    <Pressable
                        className={tableRowStyleStyle({
                            className,
                            type,
                            useGridTemplate: !!gridTemplateColumnsClass
                        })}
                        role="row"
                        testID="table-row"
                        onPress={handleRowClick}
                    >
                        {children}
                    </Pressable>
                );
            }

            return (
                <Box
                    className={tableRowStyleStyle({
                        className,
                        type,
                        useGridTemplate: !!gridTemplateColumnsClass
                    })}
                    role="row"
                    testID="table-row"
                >
                    {children}
                </Box>
            );
        }

        return (
            <TR
                // @ts-expect-error
                ref={ref}
                className={tableRowStyleStyle({
                    type,
                    class: className
                })}
                {...props}
            >
                {children}
            </TR>
        );
    }
);

const TableCell = React.forwardRef<React.ComponentRef<typeof Box>, TableCellProps>(
    ({className, children, align = "start", visibleOnHover}, ref) => {
        const {size, gridTemplateColumnsClass} = useContext(TableContext);

        return (
            <Box
                ref={ref}
                className={tableCellStyle({
                    className,
                    size,
                    useGridTemplate: !!gridTemplateColumnsClass
                })}
                role="cell"
            >
                {typeof children === "string" ||
                typeof children === "number" ||
                isReactElement(children, React.Fragment) ? (
                    <Text isTruncated className="max-w-full" variant={size === "md" ? "sm" : "xs"}>
                        {children}
                    </Text>
                ) : (
                    <Box
                        className={tableCellContentStyle({
                            align,
                            visibleOnHover
                        })}
                    >
                        {children}
                    </Box>
                )}
            </Box>
        );
    }
);

const TableCaption = React.forwardRef<React.ComponentRef<typeof Caption>, ITableCaptionProps>(
    ({className, ...props}, ref) => {
        return (
            <Caption
                // @ts-expect-error
                ref={ref}
                className={tableCaptionStyle({class: className})}
                {...props}
            />
        );
    }
);

Table.displayName = "Table";
TableHeader.displayName = "TableHeader";
TableBody.displayName = "TableBody";
TableFooter.displayName = "TableFooter";
TableHead.displayName = "TableHead";
TableRow.displayName = "TableRow";
TableCell.displayName = "TableData";
TableCaption.displayName = "TableCaption";

export {
    Table,
    TableHeader,
    TableBody,
    TableFooter,
    TableHead,
    TableRow,
    TableCell,
    TableCaption,
    TablePagination
};
